import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, MapPin, ScanFace, Ban, Lock, Mail, Fingerprint } from "lucide-react";

const UPDATED = "21 June 2026";
const SUPPORT_EMAIL = "support@smrtin.net";

const HIGHLIGHTS = [
  { icon: Fingerprint, title: "Face data for authentication only", text: "Your Master Photo is used solely to verify your identity for attendance — never for marketing or tracking." },
  { icon: MapPin, title: "Location only at attendance", text: "We collect GPS location data only when you mark attendance, to verify your presence at the assigned site." },
  { icon: ScanFace, title: "Selfies for identity only", text: "Camera / selfie images are collected for the sole purpose of identity verification via AI face-matching." },
  { icon: Ban, title: "Never sold or shared", text: "We do not sell your data, and your face data is never shared with third parties." },
  { icon: Lock, title: "Encrypted & secure", text: "Face data is stored in secure, encrypted cloud environments (MongoDB and Tigris)." },
];

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-6" data-testid={`privacy-section-${id}`}>
      <h2 className="font-head text-xl font-bold text-slate-900 mb-3">{title}</h2>
      <div className="space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="privacy-page">
      {/* Header */}
      <div className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto px-6 py-10">
          <Link to="/login" data-testid="privacy-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-300 hover:text-white transition mb-6">
            <ArrowLeft className="h-4 w-4" /> Back to sign in
          </Link>
          <div className="flex items-center gap-3">
            <img src="/logo-lg.webp" alt="Smart-In" width="48" height="48" className="h-12 w-12 rounded-xl object-cover bg-white shadow" />
            <div>
              <div className="flex items-center gap-1.5 text-indigo-300 text-xs font-bold uppercase tracking-widest">
                <ShieldCheck className="h-4 w-4" /> Smart-In
              </div>
              <h1 className="font-head text-3xl font-extrabold tracking-tight leading-tight">Privacy Policy</h1>
            </div>
          </div>
          <p className="mt-4 text-sm text-slate-300">Last updated: {UPDATED}</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-6 py-10 space-y-10">
        {/* Highlights */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="privacy-highlights">
          {HIGHLIGHTS.map((h) => (
            <div key={h.title} className="bg-white border border-slate-200 rounded-xl shadow-sm p-4 flex gap-3">
              <span className="grid place-items-center h-10 w-10 rounded-lg bg-indigo-50 text-indigo-600 flex-shrink-0">
                <h.icon className="h-5 w-5" />
              </span>
              <div>
                <p className="font-semibold text-slate-900 text-sm">{h.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{h.text}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6 sm:p-8 space-y-8">
          <Section id="intro" title="1. Introduction">
            <p>
              Smart-In ("we", "us", or "our") provides a workforce attendance solution consisting of a mobile
              application used by staff and a web administration portal used by employers. This Privacy Policy explains
              what information we collect, why we collect it, how we use and protect it, and the choices you have. By
              using the Smart-In app, you agree to the practices described in this policy.
            </p>
          </Section>

          <Section id="location" title="2. Location Data">
            <p>
              <strong>We collect GPS location data only when the user marks attendance, to verify their presence at the
              site.</strong> Your device's location is captured at the moment you clock in or clock out and is compared
              against your assigned site's geofence to confirm you are physically present.
            </p>
            <p>
              We do <strong>not</strong> track your location in the background or at any other time. Location is not
              collected when the app is closed or when you are not actively marking attendance.
            </p>
          </Section>

          <Section id="camera" title="3. Camera & Selfie Images">
            <p>
              <strong>We collect camera / selfie images for the sole purpose of identity verification via AI
              face-matching.</strong> When you mark attendance, the app captures a selfie that is compared against your
              enrolled reference photo to confirm your identity and prevent buddy-punching.
            </p>
            <p>
              These images are used only for verification. We do not use your images for advertising, profiling, or any
              purpose unrelated to attendance identity verification.
            </p>
          </Section>

          <Section id="biometric" title="4. Biometric Data and Facial Verification">
            <p>
              This section describes how Smart-In collects, uses, protects, retains, and deletes biometric information
              (facial data) in connection with attendance authentication. It supplements the sections above and, to the
              extent of any conflict regarding facial data, controls.
            </p>
            <p>
              <strong>Collection.</strong> To verify user identity for attendance, the Smart-In application collects a
              "Master Photo" (a selfie enrolled by the user) together with the live selfie images captured at each
              clock-in and clock-out. The Master Photo serves as the enrolled reference used by our AI face-matching
              engine to confirm that the person marking attendance is the authorized employee.
            </p>
            <p>
              <strong>Usage.</strong> Facial data is used <strong>exclusively for workforce authentication</strong> —
              that is, to verify a user's identity when recording attendance. This data is
              <strong> never used for marketing, advertising, behavioral profiling, or tracking</strong> of any kind.
            </p>
            <p>
              <strong>Third Parties.</strong> Your facial data is <strong>never shared, sold, rented, or otherwise
              disclosed to any third parties</strong>. It is processed solely to provide the authentication feature and
              is accessible only to your authorized employer administrators through the Smart-In portal, except where
              disclosure is strictly required by applicable law.
            </p>
            <p>
              <strong>Storage.</strong> All facial data is stored in secure, encrypted cloud environments. Structured
              records are held in <strong>MongoDB</strong> and image assets are held in <strong>Tigris</strong> object
              storage, with encryption in transit and at rest and strict role-based access controls.
            </p>
            <p>
              <strong>Retention &amp; Deletion.</strong> Facial data is retained <strong>only for as long as the user's
              account remains active</strong>. Users may delete their facial data at any time using the
              <strong> "Delete Account"</strong> button in the application. Upon a deletion request, all associated
              facial data will be <strong>permanently purged within 30 days</strong> of the request.
            </p>
          </Section>

          <Section id="use" title="5. How We Use Your Information">
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Verify your presence at the assigned work site when marking attendance.</li>
              <li>Confirm your identity through AI face-matching against your reference photo.</li>
              <li>Record attendance events (clock-in/out, status, time) for your employer's records.</li>
              <li>Send you notifications about your own attendance activity (e.g., clock-in confirmations, late alerts).</li>
            </ul>
          </Section>

          <Section id="sharing" title="6. Data Sharing & Sale">
            <p>
              <strong>We do not sell your data to third parties.</strong> Your attendance data, location and images are
              accessible only to your authorized employer administrators through the Smart-In portal. We may share data
              with service providers strictly to operate the service (for example, secure cloud hosting and the
              AI face-matching engine), and only to the extent necessary to provide the service. We may also disclose
              information where required by law.
            </p>
          </Section>

          <Section id="security" title="7. Data Security">
            <p>
              <strong>Data is stored securely on our encrypted servers.</strong> We apply administrative, technical, and
              organizational safeguards — including encryption in transit and at rest, access controls, and role-based
              permissions — to protect your information against unauthorized access, disclosure, or loss.
            </p>
          </Section>

          <Section id="retention" title="8. Data Retention">
            <p>
              We retain attendance records, location and verification images for as long as necessary to provide the
              service to your employer and to comply with legal obligations. When data is no longer required, it is
              deleted or anonymized.
            </p>
          </Section>

          <Section id="rights" title="9. Your Rights">
            <p>
              Depending on your jurisdiction, you may have the right to access, correct, or request deletion of your
              personal data. Because your employer controls your attendance records, please direct such requests to your
              employer's administrator, or contact us and we will assist in coordinating your request.
            </p>
          </Section>

          <Section id="children" title="10. Children's Privacy">
            <p>
              Smart-In is intended for use by employees in a workplace setting and is not directed to children. We do not
              knowingly collect personal information from children under 13.
            </p>
          </Section>

          <Section id="changes" title="11. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. Material changes will be reflected by updating the
              "Last updated" date above. Continued use of the app after changes take effect constitutes acceptance of
              the revised policy.
            </p>
          </Section>

          <Section id="contact" title="12. Contact Us">
            <p>If you have any questions about this Privacy Policy or our data practices, contact us at:</p>
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=Privacy%20Enquiry`}
              data-testid="privacy-contact-email"
              className="inline-flex items-center gap-2 text-indigo-600 font-semibold hover:underline"
            >
              <Mail className="h-4 w-4" /> {SUPPORT_EMAIL}
            </a>
          </Section>
        </div>

        <p className="text-center text-xs text-slate-400">© {new Date().getFullYear()} Smart-In · smrtin.net</p>
      </div>
    </div>
  );
}
